import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CardapioError,
  entrarMesa,
  fetchCardapio,
  type Cardapio,
  type MesaEntrada,
} from '../../lib/api';
import { StoreView } from '../store/StoreView';

// A sessão da mesa foi revogada (operador encerrou a comanda) ou expirou (12h): o backend responde
// 401/403/404, que o cliente HTTP traduz em CardapioError kind 'unavailable'. Nesse caso o caminho de
// recuperação é reexibir o formulário de entrar (novo PIN) — não travar o cliente na mesma tela.
function ehSessaoInvalida(e: unknown): boolean {
  return e instanceof CardapioError && e.kind === 'unavailable';
}

function storageKey(codigo: string) {
  return `vitto:mesa:${codigo}`;
}

/**
 * Chave do TOKEN da conta desta pessoa NESTA mesa, em localStorage (sobrevive ao fechamento da aba, ao
 * contrário do sessionStorage da entrada).
 *
 * Cenário bar (dono 10/09): cada pessoa abre a PRÓPRIA conta, então uma entrada sem token cria uma comanda
 * NOVA. Reescanear o QR abre uma ABA NOVA — sessionStorage vazio — e sem o token guardado isso viraria uma
 * SEGUNDA CONTA para o mesmo cliente, que o garçom cobraria errado. A chave inclui o número da mesa porque o
 * código do QR é POR LOJA: o token da Mesa 5 não vale na Mesa 6 (e o backend recusa usá-lo lá).
 */
function tokenKey(codigo: string, numeroMesa: number) {
  return `vitto:mesa:token:${codigo}:${numeroMesa}`;
}

/** Leitura/escrita do token toleram falha: em aba privada o acesso pode LANÇAR, e isso não pode impedir o
 *  cliente de entrar na mesa. Sem token, o pior caso é abrir uma conta a mais — visível e corrigível. */
function lerToken(codigo: string, numeroMesa: number): string | undefined {
  try {
    return localStorage.getItem(tokenKey(codigo, numeroMesa)) ?? undefined;
  } catch {
    return undefined;
  }
}

function guardarToken(codigo: string, numeroMesa: number, token: string) {
  try {
    localStorage.setItem(tokenKey(codigo, numeroMesa), token);
  } catch {
    /* localStorage indisponível (aba privada/cota): seguimos sem a re-entrada automática */
  }
}

export function MesaPage() {
  const { codigoPublico = '' } = useParams<{ codigoPublico: string }>();
  const [nome, setNome] = useState('');
  const [numeroMesa, setNumeroMesa] = useState('');
  const [entrada, setEntrada] = useState<MesaEntrada | null>(() => {
    try {
      const value = sessionStorage.getItem(storageKey(codigoPublico));
      return value ? (JSON.parse(value) as MesaEntrada) : null;
    } catch {
      return null;
    }
  });
  const [cardapio, setCardapio] = useState<Cardapio | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Entrada EM VOO — guarda SÍNCRONA do envio duplicado (mesma defesa em profundidade do "Enviar
   * pedido" do PDV).
   *
   * O `disabled={busy}` do botão só vale depois que o React re-renderiza, e só cobre o caminho do
   * CLIQUE: um `submit` que não passa pelo botão (o "Ir"/"Go" do teclado do celular, um
   * `requestSubmit`) entra direto no `join` e abre uma SEGUNDA CONTA para a mesma pessoa. A
   * re-entrada por token não protege aqui: no primeiro envio ainda não existe token nenhum. No bar
   * isso vira cobrança duplicada para o cliente — e o garçom não tem como saber qual conta é a boa.
   * Um ref (e não um state) porque a decisão precisa valer NO MESMO lance, antes de qualquer render.
   */
  const entradaEmVooRef = useRef(false);
  const [error, setError] = useState('');
  // QA L3 — nonce de recarga do cardápio da mesa: falha de rede/5xx no fetch travava a tela no spinner sem
  // saída; um botão "Tentar de novo" incrementa isto e reexecuta o effect de carga.
  const [recarregarTick, setRecarregarTick] = useState(0);

  // QA lote12 #8 — LIMPA a sessão morta (sessionStorage + estado) e reexibe o formulário de entrar. Sem
  // isto, com a aba aberta e a sessão revogada/expirada, o cliente só via a mensagem de erro e nunca
  // reencontrava a tela de re-login (o form só renderiza quando !entrada, e nada zerava a entrada).
  const limparSessao = useCallback(
    (motivo: string) => {
      try {
        sessionStorage.removeItem(storageKey(codigoPublico));
      } catch {
        /* sessionStorage indisponível: seguimos zerando o estado em memória */
      }
      setCardapio(null);
      setEntrada(null);
      setError(motivo);
    },
    [codigoPublico]
  );

  useEffect(() => {
    if (!entrada) return;
    setError('');
    const controller = new AbortController();
    // Canal MESA (Fase 6.1a): a mesa-QR EXIBE o preço do canal MESA, que é o que o pedido da mesa COBRA
    // (RegistrarPedidoMesaService, canal MESA) — sem mismatch entre o que o cliente vê e o que paga.
    fetchCardapio(entrada.tenantSlug, controller.signal, 'MESA')
      .then((data) => {
        setCardapio(data);
        document.title = `Mesa ${entrada.mesaNumero} · ${entrada.estabelecimento}`;
      })
      .catch((e) => {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        // Sessão/menu indisponível (401/403/404): oferece o re-login em vez de prender o cliente.
        if (ehSessaoInvalida(e)) {
          limparSessao('Sua sessão nesta mesa expirou ou foi encerrada. Entre novamente para continuar.');
          return;
        }
        setError(e instanceof Error ? e.message : 'Cardápio indisponível.');
      });
    return () => controller.abort();
  }, [entrada, limparSessao, recarregarTick]);

  async function join(e: React.FormEvent) {
    e.preventDefault();
    if (entradaEmVooRef.current) return; // 2º envio no mesmo lance: ignora (ver entradaEmVooRef)
    entradaEmVooRef.current = true;
    setBusy(true);
    setError('');
    try {
      const mesa = Number(numeroMesa);
      // Reenvia o token desta mesa, se houver: o backend devolve a conta que este cliente já tem em vez de
      // abrir outra (regressão do F5 / do QR reescaneado numa aba nova).
      const result = await entrarMesa(codigoPublico, nome, mesa, lerToken(codigoPublico, mesa));
      // Persistir é BEST-EFFORT: em aba privada (ou com cota cheia) o storage LANÇA, e uma exceção aqui
      // derrubava a entrada inteira — o cliente via "não foi possível entrar" com a conta já aberta no
      // servidor. O estado em memória é o que faz a tela avançar; o storage só evita reentrar depois.
      try {
        sessionStorage.setItem(storageKey(codigoPublico), JSON.stringify(result));
      } catch {
        /* sessionStorage indisponível: a sessão vale só nesta navegação */
      }
      guardarToken(codigoPublico, mesa, result.token);
      setEntrada(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível entrar na mesa.');
    } finally {
      // Libera o envio: em ERRO o cliente precisa poder tentar de novo (sem isto, uma falha de rede
      // deixaria o botão vivo mas o envio mudo para sempre).
      entradaEmVooRef.current = false;
      setBusy(false);
    }
  }

  if (!entrada) {
    return (
      <main className="mesa-join-shell">
        <section className="mesa-join-card">
          <div className="mesa-join-brand"><span>V</span><b>Vitto</b></div>
          <div className="mesa-join-hero">
            <span className="mesa-kicker">PEDIR NA MESA</span>
            <h1>Seu pedido,<br />do seu jeito.</h1>
            <p>Informe seu nome e o número da sua mesa para começar a pedir.</p>
          </div>
          <form onSubmit={join} className="mesa-join-form">
            <label className="mesa-field">
              <span>Como podemos chamar você?</span>
              <input
                autoFocus
                autoComplete="name"
                maxLength={80}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Seu nome"
                required
              />
            </label>
            <label className="mesa-field">
              <span>Número da mesa</span>
              <input
                className="mesa-pin"
                inputMode="numeric"
                pattern="[0-9]{1,3}"
                maxLength={3}
                value={numeroMesa}
                onChange={(e) => setNumeroMesa(e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="Ex.: 7"
                required
              />
            </label>
            {error && <p className="mesa-error" role="alert">{error}</p>}
            {/* QA-28 B-07 — o botão nascia DESABILITADO sem dizer por quê (os `required` dos inputs nunca
                disparam com o submit bloqueado): quem digitava "J" ou esquecia a mesa via um botão morto.
                Hint estático + aria-describedby explicam a regra ANTES da frustração. */}
            <p className="mesa-privacy" id="mesa-join-hint">
              Nome com ao menos 2 letras e o número da sua mesa.
            </p>
            <button
              className="loja-btn loja-btn--primary loja-btn--block loja-btn--lg"
              disabled={busy || nome.trim().length < 2 || !numeroMesa || Number(numeroMesa) < 1}
              aria-describedby="mesa-join-hint"
            >
              {busy ? 'Entrando…' : 'Abrir cardápio'}
            </button>
          </form>
          <p className="mesa-privacy">Você pode pedir quantas vezes quiser enquanto sua conta estiver aberta.</p>
        </section>
      </main>
    );
  }

  if (!cardapio) {
    // QA L3 — em ERRO (rede/5xx) mostra a falha + "Tentar de novo" em vez de girar o spinner p/ sempre.
    if (error) {
      return (
        <main className="mesa-loading">
          <p>Não foi possível carregar o cardápio da mesa.</p>
          <b>{error}</b>
          <button className="loja-btn loja-btn--primary" onClick={() => setRecarregarTick((t) => t + 1)}>
            Tentar de novo
          </button>
        </main>
      );
    }
    return <main className="mesa-loading"><span className="mesa-loader" /><p>Preparando o cardápio…</p></main>;
  }

  return (
    <StoreView
      cardapio={cardapio}
      slug={entrada.tenantSlug}
      mesa={{
        codigo: codigoPublico,
        token: entrada.token,
        entrada,
        // QA lote12 #8 — o envio do pedido (placeMesaPedido) que recebe 401 propaga por aqui para
        // limpar a sessão morta e reexibir o formulário de entrar (novo PIN).
        onSessionExpired: () =>
          limparSessao('Sua sessão nesta mesa expirou ou foi encerrada. Entre novamente para continuar.'),
        // Recarrega o cardápio NO LUGAR (o efeito de carga não zera o cardápio atual), sem sair da mesa.
        onCatalogoDesatualizado: () => setRecarregarTick((t) => t + 1),
      }}
    />
  );
}
